import { useTranslation } from "react-i18next"
import { compact, camelCase, capitalize } from "lodash-es"
import { TaxContribution } from "@oysterhr/schema"
import { QuoteItemGroup } from "../QuoteItemGroup"
import classNames from "classnames"
import { convertSubunitIntoUnit, convertUnitIntoSubunit } from "@oysterhr/common"
import { buildMoney } from "~/shared/components/VariableCompensationSummary/helpers"
import QuoteAdditionalCosts from "./QuoteAdditionalCosts"
import { FlagIcon, Icon } from "@oysterhr/ui"
import { useMemo } from "react"
import { useParams } from "react-router-dom"
import { QuoteEmploymentCardProps } from "../../types"
import {
  getAmountWithCurrencyAndCommas,
  getBenefitsDetails,
  getVATDisplayValue,
  getVATLabel,
  getVariableCompensationGroupTotal,
  isCountrySpain,
  sortCostsByGroup,
} from "../../utils/helpers"
import { QuoteCard } from "../QuoteCard"
import { QuoteCollapsableItem } from "../QuoteCollapsableItem"
import { QuoteItem } from "../QuoteItem"
import { BEST_IN_CLASS_PACKAGE_NAME, COMPETITIVE_PACKAGE_NAME } from "@oysterhr/benefits"
import { useCalculationsProvider } from "../../contexts"
import { useScaleSubscriptionForCompany } from "~/hooks/useScaleSubscription"
import { IconWithTooltip } from "@oysterhr/feature-components"

export function QuoteEmployerCard({
  annualGrossSalary,
  calculation,
  optionalCosts,
  benefits,
  cycle,
  className,
  isHiringFlow,
  monetizationPlan,
}: QuoteEmploymentCardProps) {
  const { t } = useTranslation("components/Quote")
  const { activeCalculationIndex, setIsEditMode, companyCurrencyCode } = useCalculationsProvider()
  const { companyStrongId: companyId } = useParams<{ companyStrongId: string }>()

  // Taxes
  const hasDiscount =
    (calculation.fees.oyster.feeInEngagementSalaryCurrency?.discount as number) > 0

  // VAT
  const vatValue = Number(calculation.fees.vat?.value)
  const countryName = calculation.country.name
  const countryCode = calculation.country.code
  const isSpain = isCountrySpain(countryCode)

  const showVatFields = calculation.vatOptions
  const hasVat = vatValue || showVatFields
  // If country has vat options, show percentage only after calculation, which relies on vat property.
  // Else, show percentage only if calculation has a valid value
  const showVatPercentage = calculation.vatOptions
    ? calculation.vatParameters
    : calculation.fees.vat?.rate

  // Benefits
  const benefitsDetails = getBenefitsDetails(calculation.currency.code, t, benefits)
  const {
    selectedBenefitPackage,
    selectedBenefitFee,
    hasEssentialPackage,
    hasHealthAddon,
    annualBenefitsAddonsCost,
    totalAnnualBenefitsCost,
    annualBenefitsTierFeeCost,
    annualBenefitsPlanFeeCost,
  } = benefitsDetails

  // Functions
  const getMandatoryCosts = useMemo(() => {
    const employerTaxes: TaxContribution[] = [...calculation.taxes.employer.contributions]
    let mandatoryCosts: TaxContribution[] = []
    if (selectedBenefitPackage && hasEssentialPackage) {
      employerTaxes.push(selectedBenefitPackage)
      selectedBenefitFee && employerTaxes.push(selectedBenefitFee)
      mandatoryCosts = employerTaxes.filter(tax => !tax.metadata?.benefitEssential)
    } else if (selectedBenefitPackage?.id === COMPETITIVE_PACKAGE_NAME) {
      mandatoryCosts = employerTaxes.filter(tax => !tax.metadata?.benefitCompetitive)
    } else if (selectedBenefitPackage?.id === BEST_IN_CLASS_PACKAGE_NAME) {
      mandatoryCosts = employerTaxes.filter(tax => !tax.metadata?.benefitBestInClass)
    } else {
      mandatoryCosts = [...employerTaxes]
    }

    if (hasHealthAddon) {
      mandatoryCosts = employerTaxes.filter(tax => !tax.metadata?.benefitHealthAddon)
    }

    const sortedMandatoryCosts = sortCostsByGroup(mandatoryCosts)

    const employerTaxGroups = [
      ...new Set(
        sortedMandatoryCosts.map(tax => {
          return tax["group"]
        }),
      ),
    ]

    const getTaxesGroupMap = () => {
      const costMap: Record<string, number> = {}
      sortedMandatoryCosts.map(tax => {
        const { group, amount } = tax
        if (group) {
          group in costMap ? (costMap[group] += amount) : (costMap[group] = amount)
        }
      })
      return costMap
    }

    const employerTaxesGroupMap = getTaxesGroupMap()

    const totalMandatoryCosts = Object.values(employerTaxesGroupMap).reduce(
      (total, groupValue) => total + groupValue,
      0,
    )

    return { sortedMandatoryCosts, employerTaxGroups, employerTaxesGroupMap, totalMandatoryCosts }
  }, [
    calculation.taxes.employer.contributions,
    selectedBenefitPackage,
    selectedBenefitFee,
    hasEssentialPackage,
    hasHealthAddon,
  ])

  const getTotalByGroup = (groupName: string) => {
    if (groupName in employerTaxesGroupMap) {
      return getAmountWithCurrencyAndCommas(
        employerTaxesGroupMap[groupName],
        calculation.currency.code,
        cycle,
      )
    }
    return "-"
  }

  const handleEdit = () => {
    setIsEditMode(true, activeCalculationIndex)
  }

  // Derived variables
  const { sortedMandatoryCosts, employerTaxGroups, employerTaxesGroupMap, totalMandatoryCosts } =
    getMandatoryCosts
  const selectedPackageCost =
    totalAnnualBenefitsCost + annualBenefitsTierFeeCost + annualBenefitsPlanFeeCost

  const salary = buildMoney({
    currencyCode: calculation.currency.code,
    subunit: convertUnitIntoSubunit(annualGrossSalary, calculation.currency.code),
  })

  const variableCompensationsTotal = getVariableCompensationGroupTotal(salary, optionalCosts)
  const totalAdditionalCosts =
    convertSubunitIntoUnit(variableCompensationsTotal, calculation.currency.code) +
    (hasEssentialPackage ? annualBenefitsAddonsCost : selectedPackageCost)
  const oysterFee = calculation.fees.oyster.feeInEngagementSalaryCurrency?.value

  // Oyster Scale Subscription
  const { data: scaleData } = useScaleSubscriptionForCompany(companyId)
  const scaleSubscription = scaleData?.company?.activeOrPendingScaleSubscription
  const scaleActive = monetizationPlan?.product === "scale" && scaleSubscription?.isActive

  const totalEmployerCost =
    totalMandatoryCosts + totalAdditionalCosts + annualGrossSalary + oysterFee + vatValue

  return (
    <QuoteCard
      className={classNames("h-max w-full md:mr-0", className)}
      title={t("titleCardEmployer")}
      titleAdornment={<FlagIcon countryCode={calculation.country.code} />}
      items={compact([
        <QuoteItem
          key="grossSalary"
          isCategory
          value={getAmountWithCurrencyAndCommas(
            Number(annualGrossSalary),
            calculation.currency.code,
            cycle,
          )}
          label={t("labelGrossSalaryCosts", { cycle })}
        />,
        <QuoteCollapsableItem
          key="estimated"
          isCategory
          value={getAmountWithCurrencyAndCommas(
            totalMandatoryCosts,
            calculation.currency.code,
            cycle,
          )}
          label={
            <div>
              {t("labelEstimatedTaxesContributions")}
              <IconWithTooltip
                icon="InfoCircle"
                className="ml-0.5"
                value={t("tooltipEmployerCosts")}
                size="xs"
              />
            </div>
          }
          items={employerTaxGroups.map(groupName => (
            <QuoteItemGroup
              label={t(`mandatoryCostGroups.${camelCase(groupName as string)}`) ?? ""}
              value={getTotalByGroup(groupName as string)}
              key={groupName}
              items={sortedMandatoryCosts
                .filter(tax => tax["group"] === groupName)
                .map(tax => (
                  <QuoteItem
                    className=" 0 text-xs text-gray-400"
                    key={tax.name}
                    label={tax.name}
                    value={getAmountWithCurrencyAndCommas(
                      tax.amount,
                      calculation.currency.code,
                      cycle,
                    )}
                  />
                ))}
            />
          ))}
          toggleDataAttributes={{
            "data-analytics-name": "employer-mandatory-cost-selector",
            "data-testid": "mandatory-cost-collapse",
          }}
        />,
        <QuoteAdditionalCosts
          key="additionalCosts"
          totalAdditionalCosts={totalAdditionalCosts}
          salary={salary}
          optionalCosts={optionalCosts}
          benefitsDetails={benefits ? benefitsDetails : undefined}
          currencyCode={calculation.currency.code}
          cycle={cycle}
        />,
        <QuoteItem
          className={`mb-${hasDiscount ? 0 : 2} pb-0`}
          isCategory={!scaleActive}
          key="oysterFee"
          value={
            scaleActive
              ? (t("feeIncludedOnScale") as unknown as string)
              : getAmountWithCurrencyAndCommas(
                  calculation.fees.oyster.feeInEngagementSalaryCurrency?.value,
                  calculation.currency.code,
                  cycle,
                )
          }
          {...(!scaleActive &&
            isHiringFlow &&
            companyCurrencyCode &&
            calculation.fees.oyster.companysPlanPrice &&
            companyCurrencyCode !== calculation.currency.code && {
              secondaryLabel: t("labelBilledInPreferredCurrency"),
              secondaryValue: getAmountWithCurrencyAndCommas(
                calculation.fees.oyster.companysPlanPrice?.value,
                companyCurrencyCode,
                cycle,
              ),
            })}
          label={
            <div className="flex">
              {t("labelOysterFee")} ({t("labelBilledMonthly")})
              <IconWithTooltip
                icon="InfoCircle"
                value={t("tooltipOysterFee")}
                className="ml-0.5"
                size="xs"
              />
            </div>
          }
        />,
        hasDiscount ? (
          <QuoteItem
            className="mt-0 text-sm"
            key="oysterFeeDiscount"
            value={
              <span className="text-error-darken-100">
                {getAmountWithCurrencyAndCommas(
                  Math.round(
                    calculation.fees.oyster.feeInEngagementSalaryCurrency
                      ?.withoutDiscount as number,
                  ),
                  calculation.currency.code,
                  cycle,
                )}
              </span>
            }
            label={t("eligibleDiscount", {
              discount: calculation.fees.oyster.feeInEngagementSalaryCurrency?.discountRate,
            })}
          />
        ) : null,
        hasVat ? (
          <QuoteItem
            key="vat"
            value={getAmountWithCurrencyAndCommas(
              Number(vatValue),
              calculation.currency.code,
              cycle,
            )}
            isCategory
            label={
              <div>
                {showVatPercentage
                  ? t("labelVatPercentage", {
                      vatPercentage: calculation.fees.vat?.rate?.toFixed(2) || 0,
                    })
                  : t("labelVat")}
                <IconWithTooltip
                  icon="InfoCircle"
                  value={t("tooltipVat")}
                  className="ml-0.5"
                  size="xs"
                />
              </div>
            }
          />
        ) : null,
        showVatFields && !isHiringFlow ? (
          <div className="mb-2 flex items-center justify-between">
            <span className="max-w-56 text-sm text-gray-400">
              {getVATLabel(isSpain, countryName, t, calculation.vatParameters)}
            </span>
            {calculation.vatParameters && (
              <div className="flex items-center">
                <span className="px-2 text-sm text-gray-400">
                  {capitalize(getVATDisplayValue(isSpain, calculation.vatParameters))}
                </span>
                <button
                  className="flex h-fit items-center rounded-md border-2 border-gray-200 px-2 py-1 text-xs text-gray-400"
                  onClick={handleEdit}
                >
                  <Icon name="PencilSimple" className="mr-1 text-black" />
                  {t("edit")}
                </button>
              </div>
            )}
          </div>
        ) : null,
        <QuoteItem
          key="totalEmployerCost"
          value={getAmountWithCurrencyAndCommas(
            totalEmployerCost,
            calculation.currency.code,
            cycle,
          )}
          label={
            cycle === "monthly" ? (
              <div>
                {t("labelTotalCycleCosts", { cycle })}
                <IconWithTooltip
                  icon="InfoCircle"
                  value={t("tooltipMonthlyCosts")}
                  className="ml-0.5"
                  size="xs"
                />
              </div>
            ) : (
              t("labelAnnualCycleCosts")
            )
          }
          bold
          className="mb-0"
        />,
      ])}
    />
  )
}
